import { IconButton, InputAdornment, TextFieldProps } from '@mui/material';
import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import MaskedInput, { Mask } from 'react-text-mask';
import { AppInputLabel } from './AppInputLabel';
import { StyledTextField } from './StyledComponents';
import get from 'lodash/get';
import { AlertCircleIcon } from '../Shared';
import { PreviewOnIcon } from '../../Icons/PreviewOnIcon';
import { PreviewOffIcon } from '../../Icons/PreviewOffIcon';
import { StyledErrorPlaceholder } from '../Select/StyledErrorPlaceholder';

type AppInputProps = TextFieldProps & {
	rules?: RegisterOptions;
	name: string;
	changeHandler?(value: any): void; //Custom function to get value of input
	mask?: Mask | ((value: string) => Mask);
	toUpperCase?: boolean;
	hideHelperText?: boolean;
	reservationInput?: boolean;
	requiredInput?: boolean;
	errorHelperText?: string;
};

export const AppInput = ({
	rules,
	name,
	id,
	label,
	type,
	errorHelperText,
	inputProps,
	changeHandler,
	mask,
	toUpperCase,
	hideHelperText,
	requiredInput,
	reservationInput,
	...rest
}: AppInputProps) => {
	const [showPassword, setShowPassowrd] = useState<boolean>(type === 'password' ? false : true);
	const {
		register,
		formState: { errors },
		setValue,
		trigger,
		control,
	} = useFormContext();

	const { onBlur } = register(name);
	const handleClickShowPassword = () => {
		setShowPassowrd((prev) => !prev);
	};
	const [focus, setFocus] = useState<boolean>(false);
	const handleBlur = (event: FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
		setFocus(false);
		onBlur(event);
		trigger(name);
	};

	const fieldError = get(errors, name);

	const handleHelperText = () => {
		return !!fieldError?.message || errorHelperText ? (
			<>
				<AlertCircleIcon width='0.9rem' style={{ marginRight: '3px' }} />
				{fieldError?.message || errorHelperText}
			</>
		) : (
			!hideHelperText && <StyledErrorPlaceholder />
		);
	};

	function cleanMaskedInput(value: string) {
		//this works for pesel and idNumber masked inputs
		if (mask) return value.replaceAll(/_\s|_/g, '');
		return value;
	}

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		let value = toUpperCase ? e.target.value.toUpperCase() : e.target.value;

		value = cleanMaskedInput(value);

		if (mask && !value) {
			setValue(name, '', { shouldDirty: true });
			trigger(name);
		} else {
			setValue(name, value, { shouldDirty: true });
			trigger(name);
		}

		if (changeHandler) changeHandler(value);
	};

	return (
		<>
			<AppInputLabel
				required={requiredInput || reservationInput}
				name={name}
				focus={focus}
				label={label}
				error={!!fieldError}
			/>

			{!mask ? (
				<StyledTextField
					variant='outlined'
					id={name}
					fullWidth
					type={showPassword ? 'text' : type}
					inputProps={inputProps}
					error={!!fieldError || !!errorHelperText}
					helperText={handleHelperText()}
					InputProps={{
						endAdornment: type === 'password' && !rest.disabled && (
							<InputAdornment position='end' style={{ marginRight: '27px' }}>
								<IconButton
									aria-label='toggle password visibility'
									onClick={handleClickShowPassword}
									tabIndex={-1}
									edge='end'
									style={{ padding: '0' }}
								>
									{showPassword ? <PreviewOnIcon /> : <PreviewOffIcon />}
								</IconButton>
							</InputAdornment>
						),
					}}
					onFocus={() => {
						setFocus(true);
					}}
					{...register(name, rules)}
					onBlur={handleBlur}
					onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleOnChange(e)}
					{...rest}
				/>
			) : (
				<Controller
					name={name}
					control={control}
					rules={rules}
					render={({ field: { value } }) => (
						<MaskedInput
							value={value}
							mask={mask}
							guide={true}
							id={name}
							onFocus={() => {
								setFocus(true);
							}}
							onBlur={handleBlur}
							onChange={(e) => handleOnChange(e)}
							render={(ref, props) => (
								<StyledTextField
									{...props}
									inputRef={ref}
									variant='outlined'
									id={name}
									fullWidth
									type={showPassword ? 'text' : type}
									inputProps={inputProps}
									error={!!fieldError}
									helperText={handleHelperText()}
									{...rest}
								/>
							)}
						/>
					)}
				/>
			)}
		</>
	);
};
